import React, { memo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Cell } from 'recharts';
import { PipelineItem, DateRange } from '../types';
import { filterDataByDateRange, countItemsByPipeline, getPreviousPeriodComparison } from '../utils/dataUtils';
import { FileText, Mail, TrendingUp, TrendingDown, BoxIcon } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartConfig, ChartContainer } from './ui/chart';
// Define color constants
const COLORS = {
  forms: {
    current: '#515B3A',
    previous: '#515B3A80' // 50% opacity
  },
  letters: {
    current: '#F78154',
    previous: '#F7815480' // 50% opacity
  },
  memos: {
    current: '#553E4E',
    previous: '#553E4E80' // 50% opacity
  }
};
// Legend colors for the key at the bottom
const LEGEND_COLORS = {
  'Previous Period': '#D1D5DB',
  'Current Period': '#4B5563' // dark grey
};
interface OverviewProps {
  data: PipelineItem[];
  dateRange: DateRange;
}
const Overview: React.FC<OverviewProps> = ({
  data,
  dateRange
}) => {
  const filteredData = filterDataByDateRange(data, dateRange);
  const draftedCounts = countItemsByPipeline(filteredData, 'firstDraftAt');
  const approvedCounts = countItemsByPipeline(filteredData, 'approvalAt');
  // Get previous period data
  const previousStartDate = new Date(dateRange.startDate.getTime() - (dateRange.endDate.getTime() - dateRange.startDate.getTime()));
  const previousEndDate = new Date(dateRange.startDate);
  const previousPeriodData = filterDataByDateRange(data, {
    ...dateRange,
    startDate: previousStartDate,
    endDate: previousEndDate
  });
  const previousDraftedCounts = countItemsByPipeline(previousPeriodData, 'firstDraftAt');
  const previousApprovedCounts = countItemsByPipeline(previousPeriodData, 'approvalAt');
  // Get trend data for both drafted and approved
  const draftTrends = getPreviousPeriodComparison(data, dateRange, 'firstDraftAt');
  const approvalTrends = getPreviousPeriodComparison(data, dateRange, 'approvalAt');
  const pipelineData = [{
    name: 'Forms',
    color: COLORS.forms.current,
    Drafted: draftedCounts.form,
    Approved: approvedCounts.form,
    'Previous Drafted': previousDraftedCounts.form,
    'Previous Approved': previousApprovedCounts.form,
    draftTrend: draftTrends.form,
    approvalTrend: approvalTrends.form,
    icon: FileText
  }, {
    name: 'Letters',
    color: COLORS.letters.current,
    Drafted: draftedCounts.letter,
    Approved: approvedCounts.letter,
    'Previous Drafted': previousDraftedCounts.letter,
    'Previous Approved': previousApprovedCounts.letter,
    draftTrend: draftTrends.letter,
    approvalTrend: approvalTrends.letter,
    icon: Mail
  }, {
    name: 'Memos',
    color: COLORS.memos.current,
    Drafted: draftedCounts.memo,
    Approved: approvedCounts.memo,
    'Previous Drafted': previousDraftedCounts.memo,
    'Previous Approved': previousApprovedCounts.memo,
    draftTrend: draftTrends.memo,
    approvalTrend: approvalTrends.memo,
    icon: BoxIcon
  }];
  // Prepare data for charts
  const draftedChartData = [{
    name: 'Forms',
    Current: draftedCounts.form,
    Previous: previousDraftedCounts.form,
    colorCurrent: COLORS.forms.current,
    colorPrevious: COLORS.forms.previous
  }, {
    name: 'Letters',
    Current: draftedCounts.letter,
    Previous: previousDraftedCounts.letter,
    colorCurrent: COLORS.letters.current,
    colorPrevious: COLORS.letters.previous
  }, {
    name: 'Memos',
    Current: draftedCounts.memo,
    Previous: previousDraftedCounts.memo,
    colorCurrent: COLORS.memos.current,
    colorPrevious: COLORS.memos.previous
  }];
  const approvedChartData = [{
    name: 'Forms',
    Current: approvedCounts.form,
    Previous: previousApprovedCounts.form,
    colorCurrent: COLORS.forms.current,
    colorPrevious: COLORS.forms.previous
  }, {
    name: 'Letters',
    Current: approvedCounts.letter,
    Previous: previousApprovedCounts.letter,
    colorCurrent: COLORS.letters.current,
    colorPrevious: COLORS.letters.previous
  }, {
    name: 'Memos',
    Current: approvedCounts.memo,
    Previous: previousApprovedCounts.memo,
    colorCurrent: COLORS.memos.current,
    colorPrevious: COLORS.memos.previous
  }];
  // Custom legend renderer
  const renderLegend = (props: any) => {
    const {
      payload
    } = props;
    return <div className="flex justify-center items-center gap-6 text-sm pt-4" data-id="element-90">
        {payload.map((entry: any, index: number) => <div key={`legend-${index}`} className="flex items-center gap-2" data-id="element-91">
            <div className="w-3 h-3 rounded-sm" style={{
          backgroundColor: LEGEND_COLORS[entry.value]
        }} data-id="element-92" />
            <span data-id="element-93">{entry.value}</span>
          </div>)}
      </div>;
  };
  return <div className="space-y-8" data-id="element-94">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-95">
        {pipelineData.map(item => <div key={item.name} className="bg-white rounded-lg border p-6 space-y-4" style={{
        borderColor: item.color
      }} data-id="element-96">
            <div className="flex items-center justify-between" data-id="element-97">
              <div className="flex items-center gap-3" data-id="element-98">
                <div className="p-2 rounded-lg" style={{
              backgroundColor: `${item.color}50`
            }} data-id="element-99">
                  <item.icon className="w-5 h-5" style={{
                color: item.color
              }} data-id="element-100" />
                </div>
                <h3 className="font-medium" data-id="element-101">{item.name}</h3>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-102">
              <div data-id="element-103">
                <div className="flex items-center gap-2" data-id="element-104">
                  <div className="text-2xl font-semibold" data-id="element-105">{item.Drafted}</div>
                  <div className={`text-sm font-medium ${item.draftTrend >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-106">
                    {item.draftTrend >= 0 ? '↑' : '↓'}{' '}
                    {Math.abs(item.draftTrend)}%
                  </div>
                </div>
                <div className="text-sm text-gray-500" data-id="element-107">Drafted</div>
              </div>
              <div data-id="element-108">
                <div className="flex items-center gap-2" data-id="element-109">
                  <div className="text-2xl font-semibold" data-id="element-110">{item.Approved}</div>
                  <div className={`text-sm font-medium ${item.approvalTrend >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-111">
                    {item.approvalTrend >= 0 ? '↑' : '↓'}{' '}
                    {Math.abs(item.approvalTrend)}%
                  </div>
                </div>
                <div className="text-sm text-gray-500" data-id="element-112">Approved</div>
              </div>
            </div>
          </div>)}
      </div>
      {/* Charts side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-113">
        {/* Drafted Chart */}
        <Card data-id="element-114">
          <CardHeader data-id="element-115">
            <CardTitle data-id="element-116">Drafted Documents</CardTitle>
          </CardHeader>
          <CardContent data-id="element-117">
            <div className="aspect-[4/3]" data-id="element-118">
              <ResponsiveContainer width="100%" height={400} data-id="element-119">
                <BarChart data={draftedChartData} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }} barGap={4} data-id="element-120">
                  <CartesianGrid vertical={false} strokeDasharray="3 3" data-id="element-121" />
                  <XAxis dataKey="name" tickLine={false} axisLine={false} data-id="element-122" />
                  <YAxis axisLine={false} tickLine={false} data-id="element-123" />
                  <Legend content={renderLegend} data-id="element-124" />
                  <Bar name="Previous Period" dataKey="Previous" radius={[4, 4, 0, 0]} barSize={25} isAnimationActive={false} data-id="element-125">
                    {draftedChartData.map((entry, index) => <Cell key={`cell-previous-${index}`} fill={entry.colorPrevious} data-id="element-126" />)}
                  </Bar>
                  <Bar name="Current Period" dataKey="Current" radius={[4, 4, 0, 0]} barSize={25} isAnimationActive={false} data-id="element-127">
                    {draftedChartData.map((entry, index) => <Cell key={`cell-current-${index}`} fill={entry.colorCurrent} data-id="element-128" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        {/* Approved Chart */}
        <Card data-id="element-129">
          <CardHeader data-id="element-130">
            <CardTitle data-id="element-131">Approved Documents</CardTitle>
          </CardHeader>
          <CardContent data-id="element-132">
            <div className="aspect-[4/3]" data-id="element-133">
              <ResponsiveContainer width="100%" height={400} data-id="element-134">
                <BarChart data={approvedChartData} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }} barGap={4} data-id="element-135">
                  <CartesianGrid vertical={false} strokeDasharray="3 3" data-id="element-136" />
                  <XAxis dataKey="name" tickLine={false} axisLine={false} data-id="element-137" />
                  <YAxis axisLine={false} tickLine={false} data-id="element-138" />
                  <Legend content={renderLegend} data-id="element-139" />
                  <Bar name="Previous Period" dataKey="Previous" radius={[4, 4, 0, 0]} barSize={25} isAnimationActive={false} data-id="element-140">
                    {approvedChartData.map((entry, index) => <Cell key={`cell-previous-${index}`} fill={entry.colorPrevious} data-id="element-141" />)}
                  </Bar>
                  <Bar name="Current Period" dataKey="Current" radius={[4, 4, 0, 0]} barSize={25} isAnimationActive={false} data-id="element-142">
                    {approvedChartData.map((entry, index) => <Cell key={`cell-current-${index}`} fill={entry.colorCurrent} data-id="element-143" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
};
export default Overview;